import { useState, type ReactNode } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { cn } from '@/lib/utils';

import { IntegrationsSection } from './IntegrationsSection';
import { OrganizationSection } from './OrganizationSection';
import { ProductPrioritySection } from './ProductPrioritySection';
import { RolesPermissionsSection } from './RolesPermissionsSection';
import { SecuritySection } from './SecuritySection';

/**
 * Settings screen (SCRUM-460) — the v7 two-column layout: a sticky left rail of
 * sections and the active section's content. Ports `renderSettingsPage` +
 * SETTINGS_SECTIONS (app-shell 7.html). Rendered by the AppShell for the Bank
 * Executive (SideNav footer entry).
 *
 * The Roles & permissions section is delivered in SCRUM-461; until then its rail
 * item renders a short placeholder so the rail stays complete.
 */

type SectionKey = 'org' | 'security' | 'rbac' | 'products' | 'integrations';

interface SectionDef {
  key: SectionKey;
  label: string;
  desc: string;
  icon: ReactNode;
}

const SECTIONS: readonly SectionDef[] = [
  {
    key: 'org',
    label: 'Organization',
    desc: 'Bank profile and admin contacts',
    icon: (
      <>
        <rect x="2.5" y="6" width="11" height="7" rx="1" />
        <polyline points="6 6 6 4 10 4 10 6" />
      </>
    ),
  },
  {
    key: 'security',
    label: 'Security & auth',
    desc: 'Sessions and policy (MFA via your IdP)',
    icon: (
      <>
        <rect x="3.5" y="6.5" width="9" height="7" rx="1" />
        <path d="M5 6.5V4.5a3 3 0 0 1 6 0v2" />
      </>
    ),
  },
  {
    key: 'rbac',
    label: 'Roles & permissions',
    desc: 'Roles, permissions, and user assignment',
    icon: (
      <>
        <circle cx="6" cy="6" r="2" />
        <path d="M2.5 13c0-1.8 1.5-3 3.5-3s3.5 1.2 3.5 3" />
        <circle cx="11" cy="5" r="1.5" />
        <path d="M13.5 11c0-1.4-1-2.3-2.5-2.3" />
      </>
    ),
  },
  {
    key: 'products',
    label: 'Product priority',
    desc: 'Order products appear in recommendations',
    icon: (
      <>
        <rect x="3" y="3" width="4" height="4" rx="1" />
        <rect x="3" y="9" width="4" height="4" rx="1" />
        <line x1="9" y1="5" x2="13" y2="5" />
        <line x1="9" y1="11" x2="13" y2="11" />
      </>
    ),
  },
  {
    key: 'integrations',
    label: 'Integrations',
    desc: 'Widgets and webhooks',
    icon: (
      <>
        <circle cx="4" cy="8" r="1.5" />
        <circle cx="12" cy="4" r="1.5" />
        <circle cx="12" cy="12" r="1.5" />
        <line x1="5.3" y1="7.3" x2="10.7" y2="4.5" />
        <line x1="5.3" y1="8.7" x2="10.7" y2="11.5" />
      </>
    ),
  },
];

export function SettingsPage() {
  const [active, setActive] = useState<SectionKey>('org');

  return (
    <div className="flex flex-col">
      <PageHeader title="Settings" subtitle="Organization, users, roles, and integrations" />

      <div className="p-tk-2xl">
        <div className="gap-tk-2xl grid w-full grid-cols-1 items-start md:grid-cols-[240px_1fr]">
          <nav
            aria-label="Settings navigation"
            className="top-tk-md flex flex-row flex-wrap gap-1 md:sticky md:flex-col"
          >
            {SECTIONS.map((s) => {
              const isActive = s.key === active;
              return (
                <button
                  key={s.key}
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    setActive(s.key);
                  }}
                  className={cn(
                    'rounded-tk-md font-body flex items-start gap-2.5 border border-transparent px-3 py-2.5 text-left transition-colors',
                    isActive
                      ? 'bg-bg-4 border-border-2 text-text-0 shadow-tk-sm'
                      : 'text-text-2 hover:bg-bg-3 hover:text-text-0',
                  )}
                >
                  <svg
                    viewBox="0 0 16 16"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    className="mt-0.5 flex-shrink-0"
                  >
                    {s.icon}
                  </svg>
                  <div className="min-w-0 flex-1">
                    <div className="text-[0.8125rem] leading-tight font-semibold">{s.label}</div>
                    <div className="text-text-3 mt-0.5 text-[0.6875rem] leading-snug">{s.desc}</div>
                  </div>
                </button>
              );
            })}
          </nav>

          <div className="min-w-0">
            {active === 'org' && <OrganizationSection />}
            {active === 'security' && <SecuritySection />}
            {active === 'integrations' && <IntegrationsSection />}
            {active === 'rbac' && <RolesPermissionsSection />}
            {active === 'products' && <ProductPrioritySection />}
          </div>
        </div>
      </div>
    </div>
  );
}
